/**
 * Manages operations related to image fields using MT9's AssetUploader.
 * @class
 */
import { removeHost } from './removeHost.js';
import { getDataApiUrl } from './getDataApiUrl.js';

export class ImageField {
    /**
     * ImageField クラスを生成するためのコンストラクタ。
     *
     * @class
     * @param {string} id - 画像フィールドのID。この引数は必須です。
     * @param {string} handle - フィールドハンドル
     * @param {object} options - オプション
     * @param {number} options.thumbnailWidth - サムネイルの幅
     * @param {number} options.thumbnailHeight - サムネイルの高さ
     * @param {string} options.uploadDirectory - アップロードディレクトリ
     * @param {object} storage - Svelteのストレージオブジェクト
     * @param {string} storageKey - ストレージキー
     * @param {Function} triggerInputChange - input変更イベントを発火する関数
     * @throws {Error} id パラメータが指定されていない場合、エラーをスローします。
     */
    constructor(id, handle = '', options = {}, storage = null, storageKey = '', triggerInputChange = null) {
        if (!id) {
            throw new Error('The "id" parameter is required as the first argument.');
        }
        this.id = id;
        this.handle = handle;
        this.thumbnailWidth = options.thumbnailWidth || 120;
        this.thumbnailHeight = options.thumbnailHeight || 120;
        this.uploadDirectory = options.uploadDirectory || '';
        this.storage = storage;
        this.storageKey = storageKey;
        this.triggerInputChange = triggerInputChange;
    }

    //----------------------------------------
    // 静的プロパティ
    //----------------------------------------

    /** @static @property {boolean} initialized - 初期化済みフラグ */
    static initialized = false;

    //----------------------------------------
    // 静的メソッド
    //----------------------------------------

    /**
     * ImageField クラスの初期化メソッド。
     * AssetUploaderのinsertメソッドを上書きしてアセット選択イベントを発火します。
     *
     * @static
     */
    static init() {
        if (ImageField.initialized) {
            return;
        }

        // MT.AssetUploaderが利用可能かチェック
        // 注: MT9のスクリプトがまだ読み込まれていない可能性があるため、
        // 利用不可の場合は何もせずにリターンする（警告は出さない）
        if (!window.MT?.AssetUploader) {
            return;
        }

        // AssetUploaderのopenメソッドを上書き
        const originalOpen = window.MT.AssetUploader.open;

        window.MT.AssetUploader.open = (opts) => {
            const originalInsert = opts.insert;

            // insertメソッドをラップしてアセット選択時にイベントを発火
            opts.insert = async (data) => {
                // 元のinsert処理を実行
                if (originalInsert) {
                    await originalInsert(data);
                }

                // アセット情報を取得してカスタムイベントを発火
                const assetInfo = data.map((item) => ({
                    id: item.asset.id,
                    url: item.asset.url,
                    label: item.asset.label,
                    width: item.asset.width,
                    height: item.asset.height,
                    class: item.asset.class,
                    mimeType: item.asset.mimeType
                }));

                window.dispatchEvent(
                    new CustomEvent('mtapp:assetSelected', {
                        detail: { assets: assetInfo }
                    })
                );
            };

            return originalOpen(opts);
        };

        // uploadAsset関数を上書きしてアップロード完了時にイベントを発火
        if (window.MT?.export) {
            window.MT.export('uploadAsset', ({ original }) => async (args) => {
                const result = await original(args);

                // アップロード完了後にアセットIDを取得
                if (result && result.ok) {
                    const responseData = await result.json();
                    if (responseData.result && responseData.result.asset) {
                        const asset = responseData.result.asset;

                        // カスタムイベントを発火
                        window.dispatchEvent(
                            new CustomEvent('mtapp:assetUploaded', {
                                detail: {
                                    assetId: asset.id,
                                    asset: asset
                                }
                            })
                        );
                    }
                }

                return result;
            });
        }

        ImageField.initialized = true;
    }

    /**
     * 指定された要素の値を設定し、'input' と 'blur' イベントをディスパッチします。
     *
     * @static
     * @param {HTMLInputElement} el - 値を設定する対象のDOM要素。
     * @param {string|number} value - 要素に設定する値。
     */
    static setElementValue(el, value) {
        if (el) {
            el.value = value;
            el.dispatchEvent(new Event('input'));
            el.dispatchEvent(new Event('blur'));
        }
    }

    /**
     * アセット情報を取得する
     *
     * @static
     * @param {string|number} assetId - アセットID
     * @returns {Promise<Object>} アセット情報
     */
    static async getAssetInfo(assetId) {
        try {
            const apiUrl = getDataApiUrl();
            const url = `${apiUrl}/assetinfo/${assetId}`;
            const response = await fetch(url, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error.message);
            }

            return data;
        } catch (error) {
            console.error('Error fetching asset info:', error);
            throw error;
        }
    }

    /**
     * サムネイルURLを取得する
     *
     * @static
     * @param {string|number} siteId - サイトID
     * @param {string|number} assetId - アセットID
     * @param {number} width - サムネイルの幅
     * @param {number} height - サムネイルの高さ
     * @returns {Promise<string>} サムネイルURL
     */
    static async getThumbnailUrl(siteId, assetId, width, height) {
        try {
            const apiUrl = getDataApiUrl();
            const params = new URLSearchParams({
                width: String(width),
                height: String(height)
            });
            const url = `${apiUrl}/sites/${siteId}/assets/${assetId}/thumbnail?${params}`;
            const response = await fetch(url, {
                method: 'GET'
            });

            if (!response.ok) {
                throw new Error(`HTTP error! status: ${response.status}`);
            }

            const data = await response.json();

            if (data.error) {
                throw new Error(data.error.message);
            }

            return data.url;
        } catch (error) {
            console.error('Error fetching thumbnail:', error);
            throw error;
        }
    }

    //----------------------------------------
    // インスタンスプロパティ
    //----------------------------------------

    id = '';
    handle = '';
    thumbnailWidth = 120;
    thumbnailHeight = 120;
    uploadDirectory = '';
    storage = null;
    storageKey = '';
    triggerInputChange = null;

    //----------------------------------------
    // インスタンスメソッド
    //----------------------------------------

    /**
     * 画像を選択するためのメソッド。
     * MT9のAssetUploaderダイアログを開きます。
     * @method
     * @this ImageField
     */
    select() {
        // MT.AssetUploaderが利用可能かチェック
        if (!window.MT?.AssetUploader) {
            alert('AssetUploader is not available. This feature requires MT9 or later.');
            return false;
        }

        const targetIdElement = document.getElementById(this.id);
        const targetUrlElement = document.getElementById(this.id + '-url');
        const targetThumbElement = document.getElementById(this.id + '-thumb');
        const updateItemIdButton = targetIdElement?.previousElementSibling;

        // AssetUploaderダイアログを開く
        window.MT.AssetUploader.open({
            // 画像のみフィルタ
            filter: {
                class: 'image'
            },
            // アップロード先ディレクトリ
            ...(this.uploadDirectory && { uploadDirectory: this.uploadDirectory }),
            // アセット選択時のコールバック
            insert: async (data) => {
                if (data.length === 0) {
                    return;
                }

                // 最初のアセットを使用（単一選択を想定）
                const item = data[0];

                // アセット情報を正規化
                const asset = {
                    id: item.assetId,
                    url: item.assetUrl,
                    class: 'image',
                    width: item.assetWidth,
                    height: item.assetHeight
                };

                // サムネイルURLをData API経由で取得
                try {
                    // まずアセット情報を取得してサイトIDを確認
                    const assetInfo = await ImageField.getAssetInfo(asset.id);
                    const assetSiteId = assetInfo.siteId || window.mtappVars.blog_id;

                    // アセットが所属するサイトIDでサムネイルを取得
                    const thumbnailUrl = await ImageField.getThumbnailUrl(
                        assetSiteId,
                        asset.id,
                        this.thumbnailWidth,
                        this.thumbnailHeight
                    );
                    asset.thumbnail = thumbnailUrl;
                } catch (error) {
                    console.error('Error getting asset info or thumbnail:', error);
                    // エラー時は元のURLをフォールバック
                    asset.thumbnail = asset.url;
                }

                // Svelteのストレージを更新
                if (this.storage && this.storageKey) {
                    this.storage[this.storageKey].id = asset.id;
                    this.storage[this.storageKey].url = removeHost(asset.url);

                    // API経由で取得したサムネイルURLを使用
                    if (asset.thumbnail) {
                        this.storage[this.storageKey].thumbnail = removeHost(asset.thumbnail);
                    }

                    // 変更イベントを発火
                    if (this.triggerInputChange) {
                        this.triggerInputChange();
                    }
                }

                // hiddenフィールドも更新（互換性のため）
                if (targetIdElement) {
                    ImageField.setElementValue(targetIdElement, asset.id);
                }

                if (targetUrlElement && asset.url) {
                    ImageField.setElementValue(targetUrlElement, removeHost(asset.url));
                }

                if (targetThumbElement && asset.thumbnail) {
                    ImageField.setElementValue(targetThumbElement, removeHost(asset.thumbnail));
                }

                // MultiFieldProの更新ボタンをクリック
                if (updateItemIdButton instanceof HTMLElement) {
                    updateItemIdButton.click();
                }
            }
        });

        return false;
    }

    /**
     * 画像を削除するメソッド
     */
    remove() {
        const idField = document.getElementById(this.id);
        if (idField) {
            ImageField.setElementValue(idField, '');
        }

        const thumbField = document.getElementById(this.id + '-thumb');
        if (thumbField) {
            ImageField.setElementValue(thumbField, '');
        }

        const urlField = document.getElementById(this.id + '-url');
        if (urlField) {
            ImageField.setElementValue(urlField, '');
        }
    }
}
